import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ManagerSuggestion } from '../../../hooks/data/useManagerSuggestions';
import {
    createVacationRequestFormSchema,
    vacationRequestFormDefaults,
    type VacationRequestFormValues,
} from '../../../models/vacation.schemas';
import { useClientEmailFields } from './useClientEmailFields';
import {
    allowedFirstEndDates,
    buildSegments,
    isFractionedModality,
    totalEnjoyedDays,
} from '../../../utils/vacationFractioning';
import {
    SELL_DAYS,
    canSubmitRequest,
    validateRequest,
    resolvePeriodDays,
} from '../../../utils/vacationRules';
import { partitionVacationIssues } from '../../../utils/vacationIssues';
import type { VacationBalance, VacationModality } from '../../../models/vacation.types';
import type { VacationRequestFormResult } from '../vacationRequestForm.types';
import { useVacationDateBounds } from './useVacationDateBounds';
import { useVacationFormValidation } from './useVacationFormValidation';
import { usePartialBalanceModality } from './usePartialBalanceModality';

export interface UseVacationRequestFormParams {
    balance: VacationBalance | null;
    isSubmitting: boolean;
    onSubmit: (result: VacationRequestFormResult) => void;
    earliestStartDate?: string | null;
    scheduleLimitDate?: string | null;
    holidays?: string[];
    managerSuggestions: ManagerSuggestion[];
    initialValues?: Partial<VacationRequestFormValues>;
}

export function useVacationRequestForm({
    balance,
    isSubmitting,
    onSubmit,
    earliestStartDate,
    scheduleLimitDate,
    holidays = [],
    managerSuggestions,
    initialValues,
}: UseVacationRequestFormParams) {
    const schema = useMemo(
        () => createVacationRequestFormSchema({ earliestStartDate, scheduleLimitDate, holidays }),
        [earliestStartDate, scheduleLimitDate, holidays]
    );

    const {
        control,
        handleSubmit,
        register,
        setValue,
        watch,
        formState: { errors, touchedFields, dirtyFields, isSubmitted },
    } = useForm<VacationRequestFormValues>({
        resolver: zodResolver(schema),
        defaultValues: { ...vacationRequestFormDefaults, ...initialValues },
        mode: 'onChange',
    });

    useEffect(() => {
        const firstSuggestion = managerSuggestions[0];
        if (firstSuggestion && !dirtyFields.managerEmail) {
            setValue('managerEmail', firstSuggestion.collaborator.email, { shouldValidate: true });
        }
    }, [managerSuggestions, setValue, dirtyFields.managerEmail]);

    const { clientEmails, clientEmailsError, addClientEmail, removeClientEmail } = useClientEmailFields({
        control,
        errors,
    });

    const getFieldError = (name: keyof VacationRequestFormValues): string | undefined => {
        if (!isSubmitted && !touchedFields[name] && !dirtyFields[name]) return undefined;
        return errors[name]?.message as string | undefined;
    };

    const modality = watch('modality');
    const { isPartialBalance } = usePartialBalanceModality({
        balance,
        modality,
        setValue,
    });
    const firstStartDate = watch('firstStartDate');
    const firstEndDate = watch('firstEndDate');
    const secondStartDate = watch('secondStartDate');
    const sellDaysRequested = watch('sellDaysRequested');

    const isFractioned = isFractionedModality(modality);

    const periodDays = useMemo(
        () => resolvePeriodDays(balance?.balanceDays),
        [balance?.balanceDays]
    );

    const segments = useMemo(
        () =>
            buildSegments({
                modality,
                firstStartDate,
                firstEndDate,
                secondStartDate,
                sellDaysRequested,
                periodDays,
            }),
        [modality, firstStartDate, firstEndDate, secondStartDate, sellDaysRequested, periodDays]
    );

    const totalDays = totalEnjoyedDays(segments);
    const secondEndDate = segments[1]?.endDate ?? '';

    const firstEndOptions = useMemo(
        () => allowedFirstEndDates(modality, firstStartDate),
        [modality, firstStartDate]
    );

    const canSelectFirstEnd = isFractioned && Boolean(firstStartDate);
    const canSelectSecondStart = isFractioned && Boolean(firstEndDate);

    const {
        firstStartDefaultMonth,
        secondStartDefaultMonth,
        isStartDateSelectable,
        isSecondStartSelectable,
    } = useVacationDateBounds({ earliestStartDate, scheduleLimitDate, firstEndDate, holidays });

    const handleModalityChange = (next: VacationModality) => {
        setValue('modality', next, { shouldValidate: true });
        setValue('firstEndDate', '', { shouldValidate: true });
        setValue('secondStartDate', '', { shouldValidate: true });
        if (next !== '30') {
            setValue('sellDaysRequested', false, { shouldValidate: true });
        }
    };

    const handleFirstStartChange = (iso: string) => {
        setValue('firstStartDate', iso, { shouldValidate: true });
        setValue('firstEndDate', '', { shouldValidate: true });
        setValue('secondStartDate', '', { shouldValidate: true });
    };

    const handleFirstEndChange = (iso: string) => {
        setValue('firstEndDate', iso, { shouldValidate: true });
        setValue('secondStartDate', '', { shouldValidate: true });
    };

    const handleSecondStartChange = (iso: string) => {
        setValue('secondStartDate', iso, { shouldValidate: true });
    };

    const hasRequiredDates =
        Boolean(firstStartDate) &&
        (!isFractioned || (Boolean(firstEndDate) && Boolean(secondStartDate)));

    const { panelErrors, panelWarnings, fieldErrors, fieldWarnings, rulesAllowSubmit } =
        useVacationFormValidation({
            modality,
            segments,
            sellDaysRequested,
            balance,
            scheduleLimitDate,
            hasRequiredDates,
        });

    const canSubmit = Boolean(modality) && hasRequiredDates && rulesAllowSubmit && !isSubmitting;

    const submit = handleSubmit((data) => {
        if (!data.modality || !rulesAllowSubmit) return;

        onSubmit({
            modality: data.modality,
            segments,
            totalDays,
            sellDays: data.sellDaysRequested ? SELL_DAYS : 0,
            managerEmail: data.managerEmail,
            clientEmails,
        });
    });

    return {
        control,
        register,
        getFieldError,
        clientEmails,
        clientEmailsError,
        addClientEmail,
        removeClientEmail,
        submit,
        modality,
        isFractioned,
        isPartialBalance,
        periodDays,
        sellDaysRequested,
        segments,
        totalDays,
        secondEndDate,
        firstEndOptions,
        canSelectFirstEnd,
        canSelectSecondStart,
        firstStartDefaultMonth,
        secondStartDefaultMonth,
        isStartDateSelectable,
        isSecondStartSelectable,
        panelErrors,
        panelWarnings,
        fieldErrors,
        fieldWarnings,
        canSubmit,
        handlers: {
            onModalityChange: handleModalityChange,
            onFirstStartChange: handleFirstStartChange,
            onFirstEndChange: handleFirstEndChange,
            onSecondStartChange: handleSecondStartChange,
        },
    };
}
